import React, { useState, useEffect } from 'react';
import { 
  subscribeToUserCollection, 
  deleteUserDoc, 
  updateUserDoc, 
  addUserDoc,
} from '../utils/userDataHelper';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/recipe-pages.css';

// Import centralized utilities
import { normalizeRecipe, scaleRecipe } from '../utils/recipeSchema';
import { num } from './utils/costCalculations';
import useCurrencyTools from '../hooks/useCurrencyTools';

// Updated tag system: Standard, Favorites, Try, plus descriptive tags
const RECIPE_COLLECTIONS = [
  { id: 'standard', label: 'Standard', color: '#4caf50', description: 'Production recipes - selling well' },
  { id: 'favorites', label: 'Favorites', color: '#e91e63', description: 'Testing - not yet standard' },
  { id: 'try', label: 'To Try', color: '#ff9800', description: 'Want to try these recipes' },
];

const RECIPE_TAGS = [
  { id: 'bestseller', label: 'Bestseller', color: '#ffc107' },
  { id: 'seasonal', label: 'Seasonal', color: '#ff5722' },
  { id: 'experimental', label: 'Experimental', color: '#9c27b0' },
  { id: 'gentle', label: 'Gentle', color: '#e91e63' },
  { id: 'luxury', label: 'Luxury', color: '#3f51b5' },
  { id: 'beginner', label: 'Beginner', color: '#00bcd4' },
];

const RecipeCard = ({ recipe, onDuplicateEdit, onDelete, onToggleFavorite, onUpdateTags, onScale, onUpdateCollection, onUpdateRating, onUpdateNotes }) => {
  const [expanded, setExpanded] = useState(false);
  const { formatMoney } = useCurrencyTools();
  const [showTagMenu, setShowTagMenu] = useState(false);
  const [showScaleModal, setShowScaleModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [scaleMultiplier, setScaleMultiplier] = useState(2);
  const [editNotes, setEditNotes] = useState(recipe.notes || '');
  
  const oils = recipe.oils || recipe.items || [];
  const batchMass = recipe.computedBatchMass || recipe.batchSize || 0;
  const bars = recipe.bars || 1;
  const naoh = recipe.lyeRequired;
  const water = recipe.waterWeight || 0;
  const perBar = recipe.totals?.perBar || recipe.cost?.material / bars || 0;
  const currentCollection = RECIPE_COLLECTIONS.find(c => c.id === recipe.collection);
  const meltPour = recipe.recipeType === 'melt_pour';

  return (
    <div className="rcp-card">
      {/* Collection badge */}
      {currentCollection && (
        <div className="rcp-card-collection" style={{ background: currentCollection.color }}>
          {currentCollection.label}
        </div>
      )}

      {/* Rating stars */}
      <div className="rcp-card-rating">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            onClick={(e) => { e.stopPropagation(); onUpdateRating(recipe, star); }}
            style={{ color: (recipe.rating || 0) >= star ? '#ffc107' : '#ddd' }}
          >
            ★
          </button>
        ))}
      </div>

      {/* Header - always visible */}
      <div 
        className="rcp-card-header"
        onClick={() => setExpanded(!expanded)}
        style={{ paddingTop: currentCollection ? 28 : 12 }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span className="rcp-card-title">{recipe.name || 'Unnamed Recipe'}</span>
            {recipe.version && (
              <span className="rcp-chip rcp-chip-more">v{recipe.version}</span>
            )}
          </div>
          <div className="rcp-card-subtitle">
            {batchMass.toFixed(0)}g batch • {bars} bars • {formatMoney(perBar)}/bar
          </div>
          {recipe.tags?.length > 0 && (
            <div className="rcp-card-tags">
              {recipe.tags.map(tagId => {
                const tag = RECIPE_TAGS.find(t => t.id === tagId);
                return tag ? (
                  <span key={tagId} className="rcp-card-tag" style={{ background: tag.color }}>
                    {tag.label}
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="rcp-card-subtitle">{recipe.use?.join(', ') || 'General'}</div>
          <span className={`rcp-card-arrow${expanded ? ' open' : ''}`}>▼</span>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="rcp-card-body">
          {/* Batch Summary */}
          <div className="rcp-card-grid">
            <div className="rcp-card-grid-item">
              <div className="label">Batch Mass</div>
              <div className="value">{batchMass.toFixed(0)}g</div>
            </div>
            {meltPour ? (
              <>
                <div className="rcp-card-grid-item">
                  <div className="label">Soap base</div>
                  <div className="value">{(oils[0]?.weight || 0).toFixed(0)}g</div>
                </div>
                <div className="rcp-card-grid-item">
                  <div className="label">Fragrance</div>
                  <div className="value">{recipe.meltPour?.fragrancePct ?? 0}%</div>
                </div>
                <div className="rcp-card-grid-item">
                  <div className="label">Type</div>
                  <div className="value">Melt &amp; pour</div>
                </div>
              </>
            ) : (<>
            <div className="rcp-card-grid-item">
              <div className="label">{recipe.dualLye ? 'Total alkali' : recipe.lyeType}</div>
              <div className="value">{naoh.toFixed(1)}g</div>
            </div>
            <div className="rcp-card-grid-item">
              <div className="label">Water</div>
              <div className="value">{water.toFixed(1)}g</div>
            </div>
            <div className="rcp-card-grid-item">
              <div className="label">Superfat</div>
              <div className="value">{recipe.superfat ?? 5}%</div>
            </div>
            </>)}
          </div>

          {/* Oils */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9em', marginBottom: 6 }}>{meltPour ? 'Base' : `Oils (${oils.length})`}</div>
            <div className="rcp-chips">
              {oils.slice(0, 6).map((oil, i) => (
                <span key={i} className="rcp-chip rcp-chip-oil">
                  {oil.name}: {oil.pct?.toFixed(1)}%
                </span>
              ))}
              {oils.length > 6 && <span className="rcp-chip rcp-chip-more">+{oils.length - 6} more</span>}
            </div>
          </div>

          {/* Additives */}
          {recipe.additives?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9em', marginBottom: 6 }}>Additives ({recipe.additives.length})</div>
              <div className="rcp-chips">
                {recipe.additives.map((add, i) => (
                  <span key={i} className="rcp-chip rcp-chip-additive">
                    {add.name}: {add.amount}{add.unit}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Pricing */}
          {recipe.totals && (
            <div className="rcp-pricing">
              <span>Material: {formatMoney(recipe.cost?.material || 0)}</span>
              <span>Per Bar: {formatMoney(perBar)}</span>
              <span>Selling: {formatMoney(recipe.pricingOptions?.computedSellingPerBar || recipe.pricing?.sellingPrice || 0)}</span>
            </div>
          )}

          {/* Notes */}
          {recipe.notes && (
            <div className="rcp-notes">
              <strong>Notes:</strong> {recipe.notes}
            </div>
          )}

          {/* Actions */}
          <div className="rcp-actions">
            <button className="rcp-action-btn" onClick={() => onDuplicateEdit(recipe)}>Edit</button>
            <button className="rcp-action-btn" onClick={() => setShowScaleModal(true)}>Scale</button>
            <button className="rcp-action-btn" onClick={() => setShowTagMenu(!showTagMenu)}>Tags</button>
            <button className="rcp-action-btn" onClick={() => setShowNotesModal(true)}>Notes</button>
            <a className="rcp-action-btn" href="/print" onClick={() => sessionStorage.setItem('printRecipe', JSON.stringify(recipe))}>Print</a>
            <button className="rcp-action-btn" onClick={() => { if (window.confirm('Delete this recipe?')) onDelete(recipe); }}>Delete</button>
          </div>

          {/* Collection Selector */}
          <div className="rcp-selector-panel">
            <div className="label">Move to Collection:</div>
            <div className="rcp-selector-pills">
              {RECIPE_COLLECTIONS.map(col => (
                <button
                  key={col.id}
                  className={`rcp-selector-pill${recipe.collection === col.id ? ' active' : ''}`}
                  style={{
                    borderColor: col.color,
                    background: recipe.collection === col.id ? col.color : '#fff',
                  }}
                  onClick={() => onUpdateCollection(recipe, col.id)}
                >
                  {col.label}
                </button>
              ))}
              {recipe.collection && (
                <button
                  className="rcp-selector-pill"
                  onClick={() => onUpdateCollection(recipe, null)}
                >
                  Remove
                </button>
              )}
            </div>
          </div>

          {/* Tag Menu */}
          {showTagMenu && (
            <div className="rcp-selector-panel">
              <div className="label">Select Tags:</div>
              <div className="rcp-selector-pills">
                {RECIPE_TAGS.map(tag => {
                  const isSelected = recipe.tags?.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      className={`rcp-selector-pill${isSelected ? ' active' : ''}`}
                      style={{
                        borderColor: tag.color,
                        background: isSelected ? tag.color : '#fff',
                      }}
                      onClick={() => {
                        const currentTags = recipe.tags || [];
                        const newTags = isSelected 
                          ? currentTags.filter(t => t !== tag.id)
                          : [...currentTags, tag.id];
                        onUpdateTags(recipe, newTags);
                      }}
                    >
                      {tag.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Scale Panel */}
          {showScaleModal && (
            <div className="rcp-selector-panel">
              <div className="label">Scale Recipe</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                <span>Multiply by:</span>
                {[0.5, 1.5, 2, 3, 5].map(mult => (
                  <button
                    key={mult}
                    className={`rcp-selector-pill${scaleMultiplier === mult ? ' active' : ''}`}
                    style={{
                      borderColor: '#059669',
                      background: scaleMultiplier === mult ? '#059669' : '#fff',
                      borderRadius: 6,
                    }}
                    onClick={() => setScaleMultiplier(mult)}
                  >
                    {mult}x
                  </button>
                ))}
                <input
                  type="number"
                  value={scaleMultiplier}
                  onChange={(e) => setScaleMultiplier(Number(e.target.value))}
                  step="0.5"
                  min="0.1"
                  style={{ width: 70, padding: 8, border: '1px solid #e2e8f0', borderRadius: 6 }}
                />
              </div>
              <div style={{ marginBottom: 12, fontSize: '0.9em', color: '#64748b' }}>
                <div>New batch: {(batchMass * scaleMultiplier).toFixed(0)}g</div>
                <div>New bars: ~{Math.round(bars * scaleMultiplier)}</div>
                <div>Oil weight: {num(recipe.totalOilWeight, 1000) * scaleMultiplier}g</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="rcp-action-btn" onClick={() => { onScale(recipe, scaleMultiplier); setShowScaleModal(false); }}>Create Scaled Copy</button>
                <button className="rcp-action-btn" onClick={() => setShowScaleModal(false)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Notes Panel */}
          {showNotesModal && (
            <div className="rcp-selector-panel">
              <div className="label">Recipe Notes</div>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Add your notes, observations, improvements..."
                className="rcp-filters"
                style={{
                  width: '100%',
                  minHeight: 100,
                  padding: 12,
                  border: '1px solid #bfdbfe',
                  borderRadius: 6,
                  fontSize: '0.95em',
                  resize: 'vertical',
                  marginBottom: 12,
                  fontFamily: 'rethink sans, sans-serif',
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="rcp-action-btn" onClick={() => { onUpdateNotes(recipe, editNotes); setShowNotesModal(false); }}>Save Notes</button>
                <button className="rcp-action-btn" onClick={() => { setEditNotes(recipe.notes || ''); setShowNotesModal(false); }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Created date & version */}
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#94a3b8' }}>
            <span>Created: {recipe.createdAt ? new Date(recipe.createdAt).toLocaleDateString() : 'N/A'}</span>
            {recipe.version && <span>Version: {recipe.version}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

function RecipesPage() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUse, setFilterUse] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [filterCollection, setFilterCollection] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    let unsub = () => {};
    try {
      unsub = subscribeToUserCollection('recipes', (data) => {
        // Sort by createdAt descending
        const sorted = data.sort((a, b) => {
          const aDate = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
          const bDate = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
          return bDate - aDate;
        });
        setRecipes(sorted.map(normalizeRecipe));
        setLoading(false);
      });
    } catch (err) {
      console.error('Failed to load recipes:', err);
      setLoading(false);
    }
    return () => unsub();
  }, []);

  const handleDelete = async (recipe) => {
    try {
      await deleteUserDoc('recipes', recipe.id);
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete recipe');
    }
  };

  const handleDuplicateEdit = (recipe) => {
    if (recipe.recipeType === 'melt_pour') {
      sessionStorage.setItem('editMeltPour', JSON.stringify(recipe));
      navigate('/melt-and-pour');
      return;
    }
    sessionStorage.setItem('editRecipe', JSON.stringify(recipe));
    navigate('/calculator');
  };

  const handleToggleFavorite = async (recipe) => {
    try {
      await updateUserDoc('recipes', recipe.id, { favorite: !recipe.favorite });
    } catch (err) {
      console.error('Toggle favorite failed:', err);
    }
  };

  const handleUpdateTags = async (recipe, newTags) => {
    try {
      await updateUserDoc('recipes', recipe.id, { tags: newTags });
    } catch (err) {
      console.error('Update tags failed:', err);
    }
  };

  const handleScale = async (recipe, multiplier) => {
    try {
      const scaledRecipe = {
        ...scaleRecipe(recipe, multiplier),
        name: `${recipe.name} (${multiplier}x)`,
        version: 1,
        parentRecipeId: recipe.id,
        makePrivate: true,
        isPublic: false,
        createdAt: new Date().toISOString(),
      };
      delete scaledRecipe.publicRecipeId;
      delete scaledRecipe.id;
      await addUserDoc('recipes', scaledRecipe);
      alert(`Scaled recipe "${scaledRecipe.name}" created!`);
    } catch (err) {
      console.error('Scale recipe failed:', err);
      alert('Failed to create scaled recipe');
    }
  };

  const handleUpdateCollection = async (recipe, collectionId) => {
    try {
      await updateUserDoc('recipes', recipe.id, { collection: collectionId });
    } catch (err) {
      console.error('Update collection failed:', err);
    }
  };

  const handleUpdateRating = async (recipe, rating) => {
    try {
      // Toggle off if clicking same rating
      const newRating = recipe.rating === rating ? 0 : rating;
      await updateUserDoc('recipes', recipe.id, { rating: newRating });
    } catch (err) {
      console.error('Update rating failed:', err);
    }
  };

  const handleUpdateNotes = async (recipe, notes) => {
    try {
      await updateUserDoc('recipes', recipe.id, { notes: notes });
    } catch (err) {
      console.error('Update notes failed:', err);
    }
  };

  // Filter recipes
  const filteredRecipes = recipes.filter(r => {
    const matchSearch = !searchTerm || 
      (r.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.notes || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchUse = filterUse === 'all' || (r.use || []).includes(filterUse);
    const matchTag = filterTag === 'all' || (r.tags || []).includes(filterTag);
    const matchCollection = filterCollection === 'all' || r.collection === filterCollection;
    return matchSearch && matchUse && matchTag && matchCollection;
  });

  // Sort: standard first, then favorites, then try, then by rating
  const sortedRecipes = [...filteredRecipes].sort((a, b) => {
    // Collection priority: standard > favorites > try > none
    const collOrder = { standard: 0, favorites: 1, try: 2 };
    const aOrder = collOrder[a.collection] ?? 3;
    const bOrder = collOrder[b.collection] ?? 3;
    if (aOrder !== bOrder) return aOrder - bOrder;
    // Then by rating
    if ((b.rating || 0) !== (a.rating || 0)) return (b.rating || 0) - (a.rating || 0);
    return 0;
  });

  // Get unique uses for filter
  const allUses = [...new Set(recipes.flatMap(r => r.use || []))];

  return (
    <>
      <div className="sl-page recipes-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Recipes', to: '/recipes' },
            { label: 'My Recipes' },
          ]} />
          <div className="pageheading">My Recipes</div>
          <div className="pagedescription">Browse and manage all your saved soap recipes.</div>
        </div>

        <div className="recipes-page-content borderleft">
          {/* Collection Tabs */}
          <section className="mypadding borderbottom">
            <div className="rcp-tabs">
              <button
                className={`rcp-tab${filterCollection === 'all' ? ' active' : ''}`}
                onClick={() => setFilterCollection('all')}
              >
                All ({recipes.length})
              </button>
              {RECIPE_COLLECTIONS.map(col => {
                const count = recipes.filter(r => r.collection === col.id).length;
                return (
                  <button
                    key={col.id}
                    className={`rcp-tab${filterCollection === col.id ? ' active' : ''}`}
                    onClick={() => setFilterCollection(col.id)}
                  >
                    {col.label} ({count})
                  </button>
                );
              })}
            </div>
          </section>

          {/* Search and Filters */}
          <section className="mypadding borderbottom">
            <div className="rcp-filters">
              <input
                type="text"
                placeholder="Search recipes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select value={filterUse} onChange={(e) => setFilterUse(e.target.value)}>
                <option value="all">All Uses</option>
                {allUses.map(use => (
                  <option key={use} value={use}>{use}</option>
                ))}
              </select>
              <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
                <option value="all">All Tags</option>
                {RECIPE_TAGS.map(tag => (
                  <option key={tag.id} value={tag.id}>{tag.label}</option>
                ))}
              </select>
            </div>
          </section>

          {/* Stats */}
          <section className="mypadding borderbottom">
            <div className="rcp-stats">
              <div><strong>{recipes.length}</strong> total recipes</div>
              <div><strong>{sortedRecipes.length}</strong> shown</div>
              <div><strong>{recipes.filter(r => r.collection === 'standard').length}</strong> standard</div>
              <div><strong>{recipes.filter(r => r.rating >= 4).length}</strong> highly rated</div>
            </div>
          </section>

          {/* Recipe List */}
          <section className="mypadding">
            {loading ? (
              <div className="rcp-empty">
                <p>Loading recipes...</p>
              </div>
            ) : sortedRecipes.length === 0 ? (
              <div className="rcp-empty">
                {recipes.length === 0 ? (
                  <>
                    <p>No recipes saved yet.</p>
                    <div className="rcp-empty-actions">
                      <a href="/calculator" className="rcp-action-btn">Create Recipe</a>
                      <a href="/melt-and-pour" className="rcp-action-btn">Melt &amp; Pour Recipe</a>
                      <a href="/templates" className="rcp-action-btn">Use Template</a>
                    </div>
                  </>
                ) : (
                  <p>No recipes match your filters.</p>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {sortedRecipes.map(recipe => (
                  <RecipeCard 
                    key={recipe.id} 
                    recipe={recipe} 
                    onDuplicateEdit={handleDuplicateEdit}
                    onDelete={handleDelete}
                    onToggleFavorite={handleToggleFavorite}
                    onUpdateTags={handleUpdateTags}
                    onScale={handleScale}
                    onUpdateCollection={handleUpdateCollection}
                    onUpdateRating={handleUpdateRating}
                    onUpdateNotes={handleUpdateNotes}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      <Footer />
      </div>
    </>
  );
}

export default RecipesPage;
